import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BASE}/api`;

const http = axios.create({ baseURL: API, withCredentials: true });

// Surface the server's message ("detail") instead of axios' generic one.
http.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error.response?.data?.detail;
    error.message = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : error.message;
    if (error.response?.status === 401) window.dispatchEvent(new Event("auth:expired"));
    return Promise.reject(error);
  },
);

const get = (url, params) => http.get(url, { params }).then((r) => r.data);
const post = (url, body) => http.post(url, body).then((r) => r.data);
const put = (url, body) => http.put(url, body).then((r) => r.data);
const del = (url, params) => http.delete(url, { params }).then((r) => r.data);

// Drop empty filter values so URLs (and the server's cache keys) stay clean.
export const clean = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined));

export const api = {
  // accounts
  authStatus: () => get("/auth/status"),
  me: () => get("/auth/me"),
  login: (email, password) => post("/auth/login", { email, password }),
  register: (body) => post("/auth/register", body),
  logout: () => post("/auth/logout"),
  users: () => get("/users"),
  setRole: (id, role) => put(`/users/${id}`, { role }),
  deleteUser: (id) => del(`/users/${id}`),

  // reference + settings
  catalog: () => get("/catalog"),

  // replays
  replays: (params) => get("/replays", clean(params)),
  replay: (uid) => get(`/replays/${uid}`),
  viewerHtml: (uid) => http.get(`/replays/${uid}/viewer`, { responseType: "text" }).then((r) => {
    const html = typeof r.data === "string" ? r.data : "";
    if (!/^\s*<!doctype html/i.test(html) && !/^\s*<html/i.test(html)) {
      throw new Error("The replay viewer returned an invalid document.");
    }
    return html;
  }),
  importLog: (body) => post("/replays/import-log", body),
  uploadBinary: (file, fields = {}) => {
    const form = new FormData();
    form.append("file", file);
    Object.entries(clean(fields)).forEach(([k, v]) => form.append(k, v));
    return http.post("/replays/upload", form).then((r) => r.data);
  },
  editReplay: (uid, body) => put(`/replays/${uid}`, body),
  deleteReplay: (uid) => del(`/replays/${uid}`),
  analysis: (uid) => get(`/replays/${uid}/analysis`),
  story: (uid) => get(`/replays/${uid}/story`),
  insights: (uid) => get(`/replays/${uid}/insights`),
  positions: (uid) => get(`/replays/${uid}/positions`),
  mapHeatmap: (uid) => get(`/replays/${uid}/heatmap`),
  telemetry: (uid) => get(`/replays/${uid}/telemetry`),

  // series + tournaments
  series: (params) => get("/series", clean(params)),
  seriesDetail: (id, params) => get(`/series/${id}`, clean(params)),
  createSeries: (body) => post("/series", body),
  updateSeries: (id, body) => put(`/series/${id}`, body),
  deleteSeries: (id) => del(`/series/${id}`),
  tournaments: () => get("/tournaments"),
  createTournament: (name) => post("/tournaments", { name }),
  renameTournament: (id, name) => put(`/tournaments/${id}`, { name }),
  deleteTournament: (id) => del(`/tournaments/${id}`),

  // teams + players
  teams: (params) => get("/teams", clean(params)),
  defaultTeam: () => get("/settings/default-team"),
  setDefaultTeam: (team_id) => put("/settings/default-team", { team_id }),
  team: (id) => get(`/teams/${id}`),
  createTeam: (body) => post("/teams", body),
  updateTeam: (id, body) => put(`/teams/${id}`, body),
  deleteTeam: (id) => del(`/teams/${id}`),
  players: (params) => get("/players", clean(params)),
  createPlayer: (body) => post("/players", body),
  updatePlayer: (id, body) => put(`/players/${id}`, body),
  deletePlayer: (id) => del(`/players/${id}`),
  accounts: (q) => get("/accounts", clean({ q })),

  // stats
  overview: () => get("/stats/overview"),
  teamReport: (id, params) => get(`/stats/teams/${id}`, clean(params)),
  scouting: (id, params) => get(`/stats/teams/${id}/scouting`, clean(params)),
  weekly: (id, params) => get(`/stats/teams/${id}/weekly`, clean(params)),
  prep: (params) => get("/stats/prep", clean(params)),
  lineup: (body) => post("/stats/lineup-projection", body),
  playerReport: (key, params) => get(`/stats/players/${encodeURIComponent(key)}`, clean(params)),
  playerTelemetry: (key, params) => get(`/stats/players/${encodeURIComponent(key)}/telemetry`, clean(params)),
  leaderboard: (params) => get("/stats/leaderboard", clean(params)),
  heroes: (params) => get("/stats/heroes", clean(params)),
  heroReport: (name, params) => get(`/stats/heroes/${encodeURIComponent(name)}`, clean(params)),
  lab: (params) => get("/stats/lab", clean(params)),
  ratings: (params) => get("/stats/ratings", clean(params)),
  compare: (params) => get("/stats/compare", clean(params)),
  heatmap: (params) => get("/stats/heatmap", clean(params)),
  heroLeaderboard: (hero, params) => get(`/stats/heroes/${encodeURIComponent(hero)}/leaderboard`, clean(params)),
  profile: (key, params) => get(`/stats/players/${encodeURIComponent(key)}/profile`, clean(params)),
  titles: () => get("/stats/titles"),
  simulate: (body) => post("/stats/simulate", body),
  mapCompare: (teamId, maps) => get(`/stats/teams/${teamId}/map-compare`, { maps: maps.join(",") }),

  // Live competitive data, proxied so the provider key stays server-side.
  rankedLeaderboard: (params) => get("/ranked/leaderboard", clean(params)),
  rankedPlayerCareer: (uid, params) => get(`/ranked/players/${encodeURIComponent(uid)}/career`, clean(params)),
  rankedPlayerHeroes: (uid, params) => get(`/ranked/players/${encodeURIComponent(uid)}/heroes`, clean(params)),
  rankedPlayerHistory: (uid, params) => get(`/ranked/players/${encodeURIComponent(uid)}/history`, clean(params)),
  rankedPlayerObservations: (uid, params) => get(`/ranked/players/${encodeURIComponent(uid)}/observations`, clean(params)),

  // targets
  targetMetrics: (role) => get("/targets/metrics", clean({ role })),
  targets: (params) => get("/targets", clean(params)),
  createTarget: (body) => post("/targets", body),
  updateTarget: (id, body) => put(`/targets/${id}`, body),
  deleteTarget: (id) => del(`/targets/${id}`),

  // coaching canvas
  canvasLevels: () => get("/canvas/levels"),
  fightTokens: (uid, fight) => get("/canvas/fight", { uid, fight }),
  boards: () => get("/boards"),
  board: (id) => get(`/boards/${id}`),
  createBoard: (body) => post("/boards", body),
  updateBoard: (id, body) => put(`/boards/${id}`, body),
  deleteBoard: (id) => del(`/boards/${id}`),

  // drafts
  drafts: () => get("/drafts"),
  saveDraft: (body) => post("/drafts", body),
  updateDraft: (id, body) => put(`/drafts/${id}`, body),
  deleteDraft: (id) => del(`/drafts/${id}`),

  // 2.0 hero page
  hero2Report: (name, params) => get(`/stats/hero2/${encodeURIComponent(name)}`, clean(params)),

  // 2.0 draft simulator + leaderboard
  draft2LineupData: () => get("/drafts2/lineup-data"),
  draft2UploadLineups: (payload) => post("/drafts2/lineup-data", payload),
  draft2Projection: (body) => post("/drafts2/lineup-projection", body),
  leaderboard2: (params) => get("/stats/leaderboard2", clean(params)),

  // 2.0 weekly report + scouting
  weekly2Report: (teamId, params) => get(`/stats/legacy/weekly/${teamId}`, clean(params)),
  scouting2Report: (teamId, params) => get(`/stats/legacy/scouting/${teamId}`, clean(params)),
  scouting2Player: (key, params) => get(`/stats/legacy/scouting-player/${encodeURIComponent(key)}`, clean(params)),
};

export const viewerUrl = (uid) => `${API}/replays/${uid}/viewer`;
export const logUrl = (uid) => `${API}/replays/${uid}/log`;
export const mapImageUrl = (name) => `${API}/maps/${encodeURIComponent(name)}/image`;
export const levelImageUrl = (image) => `${API}/maps/levels/${encodeURIComponent(image)}`;
export const heroPortraitUrl = (heroId) => `${API}/heroes/${heroId}/portrait`;
